import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useLanguage } from '@/hooks/use-language'
import { useWallet, type NetworkType } from '@/hooks/use-wallet'
import { toast } from 'sonner'
import { Wallet, CheckCircle2 } from 'lucide-react'

type Plan = {
  name: string
  price: number
  duration: string
  description: string
}

type PaymentOption = {
  currency: string
  network?: string
  address: string
  qrCode: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  plan: Plan | null
  onPaymentComplete: (paymentData: any) => void
}

export const PaymentModal: React.FC<Props> = ({ isOpen, onClose, plan, onPaymentComplete }) => {
  const { user } = useAuth()
  const { language } = useLanguage()
  const { wallet, connectWallet, sendPayment, isWalletInstalled, loading: walletLoading } = useWallet()
  const [loading, setLoading] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USDT (TRC20)')
  const [paymentOptions, setPaymentOptions] = useState<PaymentOption[]>([])
  const [step, setStep] = useState<'choose' | 'pay'>('choose')
  const [processingPayment, setProcessingPayment] = useState(false)

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('choose')
      setSelectedCurrency('USDT (BEP20)')
    }
  }, [isOpen])

  const CryptoIcon: React.FC<{ symbol: string }> = ({ symbol }) => {
    const s = symbol.toUpperCase()
    
    if (s === 'BTC') return (
      <img src="/icons/btc.png" alt="Bitcoin" className="w-7 h-7" />
    )
    if (s === 'ETH') return (
      <img src="/icons/eth.png" alt="Ethereum" className="w-7 h-7" />
    )
    if (s === 'USDT') return (
      <img src="/icons/usdt.png" alt="Tether" className="w-7 h-7" />
    )
    if (s === 'BNB') return (
      <img src="/icons/bnb.png" alt="Binance Coin" className="w-7 h-7" />
    )
    
    return <div className="w-7 h-7 bg-gray-400 rounded-full flex items-center justify-center text-white text-xs font-bold">{s.charAt(0)}</div>
  }

  useEffect(() => {
    if (isOpen && plan && step === 'choose') {
      loadPaymentOptions()
    }
  }, [isOpen, plan, step])

  const loadPaymentOptions = async () => {
    if (!plan) return

    setLoading(true)
    try {
      // Direcciones de wallet para Risko Pulse - Solo 3 redes: BEP20, ERC20, Polygon
      const wallets: PaymentOption[] = [
        {
          currency: 'USDT (BEP20)',
          network: 'BEP20',
          address: '0xd383bEB24Ea2632635A05290ac2E8D86E8316c04',
          qrCode: '/qr/usdt-bep20.png'
        },
        {
          currency: 'USDT (ERC20)',
          network: 'ERC20',
          address: '0xd383bEB24Ea2632635A05290ac2E8D86E8316c04',
          qrCode: '/qr/usdt-erc20.png'
        },
        {
          currency: 'USDT (Polygon)',
          network: 'Polygon',
          address: '0xd383bEB24Ea2632635A05290ac2E8D86E8316c04',
          qrCode: '/qr/usdt-polygon.png'
        },
        {
          currency: 'BNB',
          network: 'BEP20',
          address: '0xd383bEB24Ea2632635A05290ac2E8D86E8316c04',
          qrCode: '/qr/bnb.png'
        },
        {
          currency: 'ETH',
          network: 'ERC20',
          address: '0xd383bEB24Ea2632635A05290ac2E8D86E8316c04',
          qrCode: '/qr/eth.png'
        },
        {
          currency: 'POL',
          network: 'Polygon',
          address: '0xd383bEB24Ea2632635A05290ac2E8D86E8316c04',
          qrCode: '/qr/polygon.png'
        }
      ]

      setPaymentOptions(wallets)
      setSelectedCurrency('USDT (BEP20)')
    } catch (error) {
      console.error('Error loading payment options:', error)
      setPaymentOptions([])
      alert(language === 'en' ? 'Error loading payment options. Please contact support.' : 'Error cargando opciones de pago. Por favor, contacta a soporte.')
    }
    setLoading(false)
  }

  // Detectar si la cripto seleccionada es EVM-compatible (todas las opciones ahora son EVM)
  const isEVMCompatible = (currency: string): boolean => {
    return currency.includes('USDT (BEP20)') || 
           currency.includes('USDT (ERC20)') || 
           currency.includes('USDT (Polygon)') ||
           currency === 'ETH' || 
           currency === 'BNB' ||
           currency === 'POL'
  }

  // Obtener red según cripto seleccionada
  const getNetworkForCurrency = (currency: string): NetworkType | null => {
    if (currency.includes('ERC20') || currency === 'ETH') return 'ethereum'
    if (currency.includes('BEP20') || currency === 'BNB') {
      // Detectar si el usuario está en testnet
      if (wallet.network === 'bsc_testnet') {
        return 'bsc_testnet'
      }
      return 'bsc'
    }
    if (currency.includes('Polygon') || currency === 'POL') return 'polygon'
    return null
  }

  const handlePaymentSelect = async (currency: string) => {
    setSelectedCurrency(currency)
    setStep('pay')
  }

  // Manejar pago con wallet
  const handleWalletPayment = async () => {
    if (!plan) return

    if (!wallet.isConnected) {
      const connected = await connectWallet()
      if (!connected) return
    }

    const selectedPayment = paymentOptions.find(p => p.currency === selectedCurrency)
    if (!selectedPayment) {
      console.error('❌ No se encontró la opción de pago seleccionada')
      toast.error(language === 'en' ? 'Payment option not found' : 'Opción de pago no encontrada')
      return
    }

    const network = getNetworkForCurrency(selectedCurrency)

    if (!network) {
      console.error('❌ Red no soportada')
      toast.error(language === 'en' ? 'Network not supported for wallet payment' : 'Red no soportada para pago con wallet')
      return
    }

    setProcessingPayment(true)

    try {
      // Enviar pago (detecta automáticamente si es BNB nativo o USDT token)
      const result = await sendPayment(
        selectedPayment.address,
        plan.price,
        network,
        selectedCurrency // Pasar currency para detectar si es BNB nativo
      )

      if (!result.success) {
        toast.error(result.error || (language === 'en' ? 'Payment failed' : 'Pago fallido'))
        setProcessingPayment(false)
        return
      }

      // Registrar pago en Firestore y enviar a verificación automática
      const paymentData = {
        currency: selectedPayment.currency,
        address: selectedPayment.address,
        orderId: `risko_pulse_${Date.now()}`,
        txHash: result.txHash,
        blockNumber: result.blockNumber,
        network,
        method: 'wallet_connect',
        plan: plan,
        userId: user?.uid,
        userEmail: user?.email,
        autoVerify: false,
        verificationMode: 'manual_review'
      }

      toast.info(
        language === 'en'
          ? 'Transaction sent. Your payment will be reviewed manually and your license will be emailed after verification.'
          : 'Transacción enviada. Tu pago será revisado manualmente y tu licencia se enviará por email tras la verificación.'
      )

      onClose()
      setTimeout(() => {
        onPaymentComplete(paymentData)
      }, 100)
    } catch (error: any) {
      console.error('Error processing wallet payment:', error)
      toast.error(
        language === 'en'
          ? 'Error processing payment. Please contact support.'
          : 'Error al procesar el pago. Por favor contacta a soporte.'
      )
    } finally {
      setProcessingPayment(false)
    }
  }


  if (!plan) return null

  const isEn = language === 'en'

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-panel/80 backdrop-blur-xl border-border/30 shadow-2xl">
        <DialogHeader>
          <DialogTitle>
            {step === 'choose' 
              ? (isEn ? 'Select Payment Method' : 'Seleccionar Método de Pago')
              : (isEn ? 'Payment Instructions' : 'Instrucciones de Pago')
            }
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
        {/* Plan Information */}
        <div className="bg-panel/60 backdrop-blur-xl border border-primary/30 rounded-lg p-4 shadow-xl shadow-primary/20">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="text-lg font-semibold text-primary">{plan.name}</h4>
              <p className="text-text/60 text-sm">{plan.description}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-primary">
                ${plan.price}
              </div>
              <div className="text-text/60 text-sm">{plan.duration}</div>
            </div>
          </div>
        </div>

        {step === 'choose' && (
          <>
            <div>
              <h3 className="text-lg font-semibold mb-4">
                {isEn ? 'Select your cryptocurrency/network:' : 'Selecciona tu criptomoneda/red:'}
              </h3>
              <div className="space-y-3">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-text/60">
                      {isEn ? 'Loading payment options...' : 'Cargando opciones de pago...'}
                    </p>
                  </div>
                ) : paymentOptions.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-red-400 mb-4">⚠️ {isEn ? 'Error connecting to payment system' : 'Error conectando con el sistema de pagos'}</div>
                    <p className="text-text/60 mb-4">
                      {isEn 
                        ? 'We could not load payment options. Please contact support to complete your purchase securely.'
                        : 'No pudimos cargar las opciones de pago. Por favor, contacta a soporte para completar tu compra de forma segura.'}
                    </p>
                    <Button 
                      variant="outline"
                      className="border-primary/30 hover:border-primary/50 hover:bg-primary/5"
                      onClick={() => window.open('mailto:support@risko.app?subject=Error en sistema de pagos', '_blank')}
                    >
                      {isEn ? 'Contact Support' : 'Contactar Soporte'}
                    </Button>
                  </div>
                ) : (
                  paymentOptions.map((option, index) => (
                    <div
                      key={index}
                      className={`border border-primary/30 rounded-lg p-4 hover:border-primary/50 cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 hover:scale-[1.02] active:scale-[0.98] backdrop-blur-md ${
                        selectedCurrency === option.currency ? 'ring-2 ring-primary/60 bg-panel/70 shadow-md shadow-primary/20' : 'bg-panel/50'
                      }`}
                      onClick={() => handlePaymentSelect(option.currency)}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center">
                            <CryptoIcon symbol={option.currency.includes('USDT') ? 'USDT' : option.currency} />
                          </div>
                          <div>
                            <div className="font-semibold text-lg">{option.currency}</div>
                            {option.network && (
                              <div className="text-sm text-text/60">
                                {isEn ? 'Network:' : 'Red:'} {option.network}
                              </div>
                            )}
                          </div>
                        </div>
                        <Button size="sm">{isEn ? 'Select' : 'Seleccionar'}</Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {step === 'pay' && selectedCurrency && (() => {
          const selectedPayment = paymentOptions.find(p => p.currency === selectedCurrency)
          if (!selectedPayment) return null

          // Todas las opciones son EVM-compatible ahora, solo mostrar método wallet
          if (isEVMCompatible(selectedCurrency)) {
                return (
                  <div className="space-y-4">
                    {!isWalletInstalled() ? (
                      <div className="text-center py-8 space-y-4">
                        <div className="text-primary mb-4">
                          ⚠️ {isEn ? 'No wallet detected' : 'No se detectó ninguna wallet'}
                        </div>
                        <p className="text-text/60 mb-4">
                          {isEn
                            ? 'Please install MetaMask, Trust Wallet, or Coinbase Wallet to pay with your wallet.'
                            : 'Por favor instala MetaMask, Trust Wallet o Coinbase Wallet para pagar con tu wallet.'}
                        </p>
                        <div className="flex gap-2 justify-center">
                          <Button
                            onClick={() => window.open('https://metamask.io/download/', '_blank')}
                            className="gap-2"
                            variant="outline"
                          >
                            <Wallet className="w-4 h-4" />
                            MetaMask
                          </Button>
                          <Button
                            onClick={() => window.open('https://trustwallet.com/', '_blank')}
                            className="gap-2"
                            variant="outline"
                          >
                            <Wallet className="w-4 h-4" />
                            Trust Wallet
                          </Button>
                        </div>
                      </div>
                    ) : !wallet.isConnected ? (
                      <div className="text-center py-8 space-y-4">
                        <p className="text-text/60">
                          {isEn
                            ? 'Connect your wallet to pay instantly'
                            : 'Conecta tu wallet para pagar instantáneamente'}
                        </p>
                        <Button
                          onClick={() => connectWallet()}
                          disabled={walletLoading}
                          className="gap-2"
                          size="lg"
                        >
                          <Wallet className="w-4 h-4" />
                          {walletLoading
                            ? (isEn ? 'Connecting...' : 'Conectando...')
                            : (isEn ? 'Connect Wallet' : 'Conectar Wallet')}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Wallet conectada */}
                        <div className="bg-panel/60 backdrop-blur-xl border border-primary/30 rounded-lg p-4 transition-all duration-300 shadow-xl shadow-primary/20">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle2 className="w-5 h-5 text-primary" />
                            <span className="font-semibold text-primary">
                              {isEn ? 'Wallet Connected' : 'Wallet Conectada'}
                            </span>
                          </div>
                          <p className="text-sm text-foreground/70">
                            {wallet.address?.slice(0, 6)}...{wallet.address?.slice(-4)}
                          </p>
                          {wallet.network && (
                            <p className="text-xs text-foreground/60 mt-1">
                              {isEn ? 'Network:' : 'Red:'} {wallet.network.toUpperCase()}
                            </p>
                          )}
                        </div>

                        {/* Botón de pago - MÁS VISIBLE */}
                        <Button
                          onClick={handleWalletPayment}
                          disabled={processingPayment || walletLoading}
                          className="w-full gap-3 bg-gradient-to-r from-primary via-primary to-secondary hover:from-primary/90 hover:via-primary/90 hover:to-secondary/90 text-primary-foreground shadow-2xl shadow-primary/50 transition-all duration-300 hover:scale-[1.03] active:scale-[0.97] font-bold text-xl py-7 border-2 border-primary/50"
                          size="lg"
                        >
                          {processingPayment ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              {isEn ? 'Processing Payment...' : 'Procesando Pago...'}
                            </>
                          ) : (
                            <>
                              <Wallet className="w-4 h-4" />
                              {isEn ? `Pay $${plan.price} with Wallet` : `Pagar $${plan.price} con Wallet`}
                            </>
                          )}
                        </Button>

                        <Button 
                          variant="outline" 
                          onClick={() => setStep('choose')} 
                          className="w-full border-primary/30 hover:border-primary/50 hover:bg-primary/5"
                        >
                          {isEn ? 'Back' : 'Volver'}
                        </Button>
                      </div>
                    )}
                  </div>
                )
              }

              // Si no es EVM-compatible (no debería pasar con las nuevas opciones)
              return null
            })()}
        </div>
      </DialogContent>
    </Dialog>
  )
}

